import React, { useState, useEffect } from 'react';

const UsersOverTimeChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('day');
  const [chartType, setChartType] = useState('line');
  const [hoveredPoint, setHoveredPoint] = useState(null);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/users-over-time?period=${period}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const result = await response.json();
        const formattedData = result.map(item => ({
          ...item,
          date: formatDate(item._id, period),
          displayDate: formatDateForDisplay(item._id, period),
          period: item._id,
          timestamp: createTimestamp(item._id, period)
        }));
        const sortedData = formattedData.sort((a, b) => b.timestamp - a.timestamp);
        setData(sortedData);
      }
    } catch (error) {
      console.error('Error fetching users over time data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTimestamp = (dateObj, period) => {
    if (period === 'day') {
      return new Date(dateObj.year, dateObj.month - 1, dateObj.day).getTime();
    } else if (period === 'week') {
      const jan1 = new Date(dateObj.year, 0, 1);
      const weekStart = new Date(jan1.getTime() + (dateObj.week - 1) * 7 * 24 * 60 * 60 * 1000);
      return weekStart.getTime();
    } else {
      return new Date(dateObj.year, dateObj.month - 1, 1).getTime();
    }
  };

  const formatDate = (dateObj, period) => {
    if (period === 'day') {
      return `${String(dateObj.month).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`;
    } else if (period === 'week') {
      return `W${String(dateObj.week).padStart(2, '0')}`;
    } else {
      return `${dateObj.year}-${String(dateObj.month).padStart(2, '0')}`;
    }
  };

  const formatDateForDisplay = (dateObj, period) => {
    if (period === 'day') {
      return `${dateObj.year}-${String(dateObj.month).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`;
    } else if (period === 'week') {
      const year = dateObj.year;
      const week = dateObj.week;
      const jan1 = new Date(year, 0, 1);
      const daysToAdd = (week - 1) * 7 - jan1.getDay();
      const weekStart = new Date(year, 0, 1 + daysToAdd);
      const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
      const formatDateString = (date) => {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      };
      return `${formatDateString(weekStart)} to ${formatDateString(weekEnd)}`;
    } else {
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      return `${monthNames[dateObj.month - 1]} ${dateObj.year}`;
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [period]);

  useEffect(() => {
    const handleRefresh = () => {
      setLoading(true);
      fetchData();
    };
    window.addEventListener('analyticsRefresh', handleRefresh);
    return () => window.removeEventListener('analyticsRefresh', handleRefresh);
  }, [period]);

  // Prepare chart data - take last 20 points and sort chronologically for display
  const chartData = [...data].reverse().slice(-20);

  const renderChart = () => {
    if (data.length === 0) {
      return (
        <div className="h-96 flex items-center justify-center text-gray-500 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-6xl mb-4">📊</div>
            <div className="text-lg font-medium">No data available</div>
            <div className="text-sm">for the selected period</div>
          </div>
        </div>
      );
    }

    if (chartData.length === 0) {
      return (
        <div className="h-96 flex items-center justify-center text-gray-500 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-lg font-medium">No chart data available</div>
          </div>
        </div>
      );
    }

    const width = 800;
    const height = 350;
    const padding = { top: 20, right: 50, bottom: 80, left: 80 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Get max values for scaling
    const maxTotal = Math.max(...chartData.map(d => Number(d.total) || 0));
    const maxValue = Math.max(maxTotal, 1); // Ensure we don't divide by 0

    // Create scales
    const xScale = (index) => padding.left + (index / (chartData.length - 1 || 1)) * chartWidth;
    const yScale = (value) => padding.top + chartHeight - (value / maxValue) * chartHeight;

    // Prepare data points
    const totalPoints = chartData.map((d, i) => ({
      x: xScale(i),
      y: yScale(Number(d.total) || 0),
      value: Number(d.total) || 0,
      date: d.date,
      displayDate: d.displayDate,
      index: i
    }));

    const studentPoints = chartData.map((d, i) => ({
      x: xScale(i),
      y: yScale(Number(d.students) || 0),
      value: Number(d.students) || 0,
      date: d.date,
      displayDate: d.displayDate,
      index: i
    }));

    const professorPoints = chartData.map((d, i) => ({
      x: xScale(i),
      y: yScale(Number(d.professors) || 0),
      value: Number(d.professors) || 0,
      date: d.date,
      displayDate: d.displayDate,
      index: i
    }));

    // Create path strings for lines
    const createPath = (points) => {
      if (points.length === 0) return '';
      return points.reduce((path, point, i) => {
        if (i === 0) return `M ${point.x} ${point.y}`;
        return `${path} L ${point.x} ${point.y}`;
      }, '');
    };

    // Grid lines
    const yTicks = 5;
    const yGridLines = [];
    for (let i = 0; i <= yTicks; i++) {
      const y = padding.top + (i / yTicks) * chartHeight;
      const value = Math.round(maxValue * (1 - i / yTicks));
      yGridLines.push({ y, value });
    }

    const xGridLines = chartData.map((_, i) => ({
      x: xScale(i),
      label: chartData[i].date
    }));

    return (
      <div className="w-full bg-white p-6 rounded-xl border" style={{ minHeight: '500px' }}>
        <div className="flex justify-center">
          <svg width={width} height={height} className="overflow-visible">
            {/* Grid lines */}
            {yGridLines.map((line, i) => (
              <g key={`y-grid-${i}`}>
                <line
                  x1={padding.left}
                  y1={line.y}
                  x2={width - padding.right}
                  y2={line.y}
                  stroke="#e5e7eb"
                  strokeDasharray="3,3"
                />
                <text
                  x={padding.left - 10}
                  y={line.y + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="#374151"
                >
                  {line.value}
                </text>
              </g>
            ))}

            {xGridLines.map((line, i) => (
              <g key={`x-grid-${i}`}>
                <line
                  x1={line.x}
                  y1={padding.top}
                  x2={line.x}
                  y2={height - padding.bottom}
                  stroke="#e5e7eb"
                  strokeDasharray="3,3"
                />
                <text
                  x={line.x}
                  y={height - padding.bottom + 20}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#374151"
                  transform={`rotate(-45, ${line.x}, ${height - padding.bottom + 20})`}
                >
                  {line.label}
                </text>
              </g>
            ))}

            {/* Chart data */}
            {chartType === 'line' ? (
              <>
                {/* Lines */}
                <path
                  d={createPath(totalPoints)}
                  fill="none"
                  stroke="#6366f1"
                  strokeWidth="3"
                />
                <path
                  d={createPath(studentPoints)}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2"
                />
                <path
                  d={createPath(professorPoints)}
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                />

                {/* Points */}
                {totalPoints.map((point, i) => (
                  <circle
                    key={`total-${i}`}
                    cx={point.x}
                    cy={point.y}
                    r="4"
                    fill="#6366f1"
                    stroke="white"
                    strokeWidth="2"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredPoint({ ...point, type: 'total' })}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                ))}
                {studentPoints.map((point, i) => (
                  <circle
                    key={`student-${i}`}
                    cx={point.x}
                    cy={point.y}
                    r="3"
                    fill="#10b981"
                    stroke="white"
                    strokeWidth="2"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredPoint({ ...point, type: 'students' })}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                ))}
                {professorPoints.map((point, i) => (
                  <circle
                    key={`professor-${i}`}
                    cx={point.x}
                    cy={point.y}
                    r="3"
                    fill="#f59e0b"
                    stroke="white"
                    strokeWidth="2"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredPoint({ ...point, type: 'professors' })}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                ))}
              </>
            ) : (
              <>
                {/* Bars */}
                {totalPoints.map((point, i) => {
                  const barWidth = Math.max(chartWidth / chartData.length - 10, 8);
                  const barHeight = chartHeight - (point.y - padding.top);
                  return (
                    <rect
                      key={`total-bar-${i}`}
                      x={point.x - barWidth / 2}
                      y={point.y}
                      width={barWidth}
                      height={barHeight}
                      fill="#6366f1"
                      opacity="0.8"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHoveredPoint({ ...point, type: 'total' })}
                      onMouseLeave={() => setHoveredPoint(null)}
                    />
                  );
                })}
              </>
            )}

            {/* Tooltip */}
            {hoveredPoint && (
              <g>
                <rect
                  x={hoveredPoint.x - 60}
                  y={hoveredPoint.y - 50}
                  width="120"
                  height="40"
                  fill="white"
                  stroke="#e5e7eb"
                  rx="4"
                />
                <text
                  x={hoveredPoint.x}
                  y={hoveredPoint.y - 30}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#374151"
                  fontWeight="600"
                >
                  {hoveredPoint.displayDate}
                </text>
                <text
                  x={hoveredPoint.x}
                  y={hoveredPoint.y - 15}
                  textAnchor="middle"
                  fontSize="12"
                  fill="#374151"
                >
                  {hoveredPoint.type}: {hoveredPoint.value}
                </text>
              </g>
            )}
          </svg>
        </div>

        {/* Legend */}
        <div className="mt-4 flex justify-center">
          <div className="flex items-center space-x-6">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-indigo-500 rounded mr-2"></div>
              <span className="text-sm text-gray-700">Total Users</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
              <span className="text-sm text-gray-700">Students</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-yellow-500 rounded mr-2"></div>
              <span className="text-sm text-gray-700">Professors</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h3 className="text-xl font-bold text-gray-800 mb-4">User Registration Trends</h3>
        <div className="h-96 flex items-center justify-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div>
            <div className="absolute inset-0 rounded-full border-2 border-gray-200"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h3 className="text-2xl font-bold text-gray-800">User Registration Trends</h3>
          <p className="text-sm text-gray-600 mt-1">Track user growth and registration patterns over time</p>
        </div>
        <div className="flex gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-white border-2 border-gray-200 rounded-lg px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm"
          >
            <option value="day">📅 Daily View</option>
            <option value="week">📊 Weekly View</option>
            <option value="month">📈 Monthly View</option>
          </select>
          <select
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
            className="bg-white border-2 border-gray-200 rounded-lg px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm"
          >
            <option value="line">📈 Line Chart</option>
            <option value="bar">📊 Bar Chart</option>
          </select>
        </div>
      </div>
      {/* Chart Section */}
      <div className="mb-8">
        {renderChart()}
      </div>
      {/* Stats Summary Cards */}
      {data.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-6 rounded-xl text-white shadow-lg transform hover:scale-105 transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold">
                  {data.reduce((sum, item) => sum + (Number(item.total) || 0), 0)}
                </div>
                <div className="text-blue-100 text-sm font-medium mt-1">Total Registrations</div>
                <div className="text-blue-200 text-xs mt-2">
                  Across all time periods
                </div>
              </div>
              <div className="text-4xl opacity-80">👥</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-6 rounded-xl text-white shadow-lg transform hover:scale-105 transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold">
                  {data.reduce((sum, item) => sum + (Number(item.students) || 0), 0)}
                </div>
                <div className="text-green-100 text-sm font-medium mt-1">Student Registrations</div>
                <div className="text-green-200 text-xs mt-2">
                  Academic learners
                </div>
              </div>
              <div className="text-4xl opacity-80">🎓</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-yellow-500 to-orange-600 p-6 rounded-xl text-white shadow-lg transform hover:scale-105 transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold">
                  {data.reduce((sum, item) => sum + (Number(item.professors) || 0), 0)}
                </div>
                <div className="text-orange-100 text-sm font-medium mt-1">Professor Registrations</div>
                <div className="text-orange-200 text-xs mt-2">
                  Faculty members
                </div>
              </div>
              <div className="text-4xl opacity-80">👨‍🏫</div>
            </div>
          </div>
        </div>
      )}
      {/* Detailed Breakdown Table */}
      <div className="bg-gray-50 rounded-xl p-6 shadow-inner">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center">
            <span className="mr-2">📋</span>
            Detailed Breakdown
          </h4>
          <div className="text-sm text-gray-500">
            Most recent {Math.min(15, data.length)} entries
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b">
                  Period
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b">
                  Total Users
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b">
                  Students
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b">
                  Professors
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b">
                  Growth
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.slice(0, 15).map((item, index) => {
                const prevItem = data[index + 1];
                const growth = prevItem ?
                  ((item.total - prevItem.total) / prevItem.total * 100).toFixed(1) :
                  null;
                return (
                  <tr key={index} className="hover:bg-blue-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        {item.displayDate}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 border">
                        {item.total || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border">
                        🎓 {item.students || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border">
                        👨‍🏫 {item.professors || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {growth !== null ? (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          parseFloat(growth) > 0
                            ? 'bg-green-100 text-green-800'
                            : parseFloat(growth) < 0
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {parseFloat(growth) > 0 ? '↗️' : parseFloat(growth) < 0 ? '↘️' : '➡️'}
                          {Math.abs(parseFloat(growth))}%
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {data.length > 15 && (
          <div className="mt-4 text-center">
            <div className="text-sm text-gray-500 bg-white px-4 py-2 rounded-lg border border-gray-200 inline-block">
              <span className="font-medium">{data.length - 15}</span> more entries available •
              Showing most recent <span className="font-medium">15</span> of <span className="font-medium">{data.length}</span> total periods
            </div>
          </div>
        )}
      </div>
      {/* Footer Info */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-center text-xs text-gray-500 gap-2">
          <div className="flex items-center gap-4">
            <span>📊 Data excludes admin accounts</span>
            <span>🔄 Auto-refreshes on navigation</span>
          </div>
          <div>
            Last updated: {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersOverTimeChart;